import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "@app/@core-auth";
import { GetDefaultCompanyWithDetailsGQL, AdminCompany, Restaurant, CompanyEmployeeGroupInput, CompanyEmployeeGroup } from "@app/@graphql";
import { isTypeSystemDefinitionNode } from "graphql";
import { AsyncSubject, BehaviorSubject } from "rxjs";
import { Observable } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { sortByKey } from "..";

@Injectable({
    providedIn: 'root',
})
export class CompanyAdminService {
    private _companyData$: BehaviorSubject<AdminCompany>;
    private _restaurants$: BehaviorSubject<Restaurant[]>;
    private _employeeGroups$: BehaviorSubject<CompanyEmployeeGroup[]>;
    private ready$ = new AsyncSubject<boolean>();

    private _defaultCompanyId: number;
    private _defaultCompanyName: string;
    private _defaultLogoUrl: string;

    readonly _currentLanguage = "pl";
    
    constructor(
        private auth: AuthService,
        private defaultRestaurantQuery: GetDefaultCompanyWithDetailsGQL )
    {
        this._companyData$ = new BehaviorSubject<AdminCompany>(null);
        this._restaurants$ = new BehaviorSubject<Restaurant[]>([]);
        this._employeeGroups$ = new BehaviorSubject<CompanyEmployeeGroup[]>([]);

        if (this.auth.getIsCompanyAdmin()) {
            this.getDefaultCompanyDetails();
        }
    }

    public get DefaultCompanyId(): number
    {
        return this._defaultCompanyId;
    }

    public get DefaultName(): string
    {
        return this._defaultCompanyName;
    }

    public get DefaultLogoUrl(): string
    {
        return this._defaultLogoUrl;
    }

    public refreshCompanyDetails() {
        this.getDefaultCompanyDetails();
    }

    private getDefaultCompanyDetails() {
        this.defaultRestaurantQuery
            .fetch({}, { fetchPolicy: 'network-only' })
            .pipe(
            // wyciągamy obiekt lub null
            map(({ data }) => data?.defaultCompanyWithDetails ?? null),

            // jeśli null → rzuć kontrolowany błąd (przechwycimy niżej)
            tap((company) => {
                if (!company) {
                throw new Error('Brak domyślnej firmy (defaultCompanyWithDetails = null).');
                }
            }),

            // ustawiamy stan w sposób bezpieczny
            tap((company) => {
                // company tu już jest nie-null (guard wyżej)
                const firstDetails = company.details?.find(Boolean);

                this._defaultCompanyId = company.id;
                this._defaultCompanyName = firstDetails?.name ?? '';
                this._defaultLogoUrl = firstDetails?.bannerImageUrl ?? '';

                this._companyData$.next(company);
                this._restaurants$.next(company.restaurants ?? []);
                this._employeeGroups$.next(company.employeeGroups ?? []);

                this.ready$.next(true);
                this.ready$.complete();
            }),
            // na wszelki wypadek pojedyncza emisja
            // take(1) // (opcjonalnie, fetch i tak emituje raz)
            )
            .subscribe({
            error: (err) => {
                console.error('[getDefaultCompanyDetails] error:', err);
                // ustaw stabilny stan w razie błędu
                this._companyData$.next(null as any);
                this._restaurants$.next([]);
                this._employeeGroups$.next([]);
                this.ready$.next(false);
                this.ready$.complete();
            },
            });
        }

    ready(): Observable<boolean> {
        return this.ready$.asObservable();
    }

    public selectEmployeeGroups(): Observable<any[]> {
        return this._employeeGroups$.asObservable().pipe(
            map(results => results.map( (item) => {
                return {
                    id: item.id, 
                    name: item.name,
                    subsidyAmount: item.subsidy.subsidyAmount,
                    dailySubsidyLimit: item.subsidy.dailySubsidyLimit,
                    subsidyPercentage: item.subsidy.subsidyPercentage,
                    monthlyAmountLimit: item.subsidy.monthlyAmountLimit,
                    dailyAmountLimit: item.subsidy.dailyAmountLimit,
                    isOverSubsidyPaidByCompany: item.subsidy.isOverSubsidyPaidByCompany
                }
            } )),
            sortByKey('name')
        );
    }
}